import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { AsyncComboboxOption } from './AsyncCombobox.types';

interface AsyncComboboxOptionItemProps {
  option: AsyncComboboxOption;
  isSelected: boolean;
  isHighlighted: boolean;
  isMulti: boolean;
  onClick: () => void;
  renderOption?: (option: AsyncComboboxOption, selected: boolean) => React.ReactNode;
}

export function AsyncComboboxOptionItem({
  option,
  isSelected,
  isHighlighted,
  isMulti,
  onClick,
  renderOption,
}: AsyncComboboxOptionItemProps) {
  const baseClass = cn(
    'flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer select-none',
    isHighlighted && 'bg-accent text-accent-foreground',
    option.disabled && 'pointer-events-none opacity-50',
  );

  if (renderOption) {
    return (
      <div role="option" aria-selected={isSelected} onClick={onClick} className={baseClass}>
        {renderOption(option, isSelected)}
      </div>
    );
  }

  return (
    <div
      role="option"
      aria-selected={isSelected}
      onClick={option.disabled ? undefined : onClick}
      className={baseClass}
    >
      {isMulti ? (
        <div
          className={cn(
            'w-4 h-4 rounded border flex items-center justify-center shrink-0',
            isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-input',
          )}
        >
          {isSelected && <Check className="w-3 h-3" />}
        </div>
      ) : (
        <Check className={cn('w-4 h-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
      )}

      {option.color && (
        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: option.color }} />
      )}

      <div className="flex flex-col min-w-0">
        <span className="truncate">{option.label}</span>
        {option.description && (
          <span className="text-xs text-muted-foreground truncate">{option.description}</span>
        )}
      </div>
    </div>
  );
}
